import { useMemo } from 'react';
import { useBookingState, useRealTime } from '..';
import { ReservationGroupBy, useBookingAppTourStore } from './useBookingAppTourStore';
import { ReservationStatus, RestaurantReservation, UIReservationStatus } from '@core/models';
import {
  ONLY_DATE_KEY_FORMAT,
  formatUtcDate,
  formatUtcTimestamp,
  isUtcInMoment,
} from '@utils/date.util';
import moment from 'moment';

export const useExtendedBooking = () => {
  const { now } = useRealTime();
  const { reservations: restaurantReservations } = useBookingState();
  const { selectedStatus, selectedGroupdBy, selectedDate } = useBookingAppTourStore();

  const sortedReservations = useMemo<RestaurantReservation[]>(() => {
    if (restaurantReservations.length === 0) return [];
    return [...restaurantReservations].sort(
      (reservationA, reservationB) =>
        formatUtcTimestamp(reservationB.reserved_date) -
        formatUtcTimestamp(reservationA.reserved_date)
    );
  }, [restaurantReservations]);

  const sortedAndFilteredReservations = useMemo<RestaurantReservation[]>(() => {
    if (sortedReservations.length === 0) return [];
    return [...sortedReservations].filter(reservation =>
      selectedStatus === UIReservationStatus.All
        ? true
        : (reservation.status as any) === selectedStatus
    );
  }, [sortedReservations, selectedStatus]);

  const groupedReservations = useMemo<Record<string, RestaurantReservation[]>>(() => {
    if (sortedAndFilteredReservations.length === 0) return {};
    const _groupedReservations: Record<string, RestaurantReservation[]> = {};
    for (const reservation of sortedAndFilteredReservations) {
      let key;
      switch (selectedGroupdBy) {
        case ReservationGroupBy.Date:
          key = formatUtcDate(reservation.reserved_date, ONLY_DATE_KEY_FORMAT);
          break;
      }
      _groupedReservations[key] = (_groupedReservations[key] || []).concat([reservation]);
    }
    return _groupedReservations;
  }, [sortedAndFilteredReservations, selectedGroupdBy]);

  const selectedDayAllRestaurantReservations = useMemo(() => {
    const momentDate = moment.unix(selectedDate);
    return sortedReservations.filter(reservation =>
      isUtcInMoment(momentDate, reservation.reserved_date)
    );
  }, [sortedReservations, selectedDate]);

  const selectedDayFilteredRestaurantReservations = useMemo(() => {
    const momentDate = moment.unix(selectedDate);
    return sortedAndFilteredReservations.filter(reservation =>
      isUtcInMoment(momentDate, reservation.reserved_date)
    );
  }, [sortedAndFilteredReservations, selectedDate]);

  const selectedDayServingResevations = useMemo(() => {
    return selectedDayAllRestaurantReservations.filter(
      reservation => reservation.status === ReservationStatus.InProgress
    );
  }, [selectedDayAllRestaurantReservations]);

  const futureRestaurantReservations = useMemo(
    () =>
      sortedAndFilteredReservations.filter(reservation => {
        const reservedDate = moment(reservation.reserved_date);
        return reservedDate.isAfter(now);
      }),
    [sortedAndFilteredReservations, now]
  );

  return {
    groupedReservations,
    sortedReservations,
    sortedAndFilteredReservations,
    selectedDayFilteredRestaurantReservations,
    selectedDayAllRestaurantReservations,
    selectedDayServingResevations,
    futureRestaurantReservations,
  };
};
